import { useEffect, useMemo, useRef, useState } from "react";
import { HERO_CANDIDATES } from "@/lib/projectImagery";
import { selectHeroSlides, type ScoredCandidate } from "@/lib/heroAlgorithm";
import { beginSession, getTaste } from "@/lib/visitorTaste";
import { fetchGlobalPopularity } from "@/lib/globalPopularity";

// Persistent first-visit flag. We pin the `homepageHero: "first-visit"`
// candidate to slot 0 on the very first session only, then never again.
const FIRST_VISIT_KEY = "cl_hero_first_visit_done";
const isFirstVisit = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return !window.localStorage.getItem(FIRST_VISIT_KEY);
  } catch {
    return false;
  }
};
const markFirstVisitDone = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(FIRST_VISIT_KEY, "1");
  } catch {
    /* ignore */
  }
};

/**
 * El Paso Star must appear in EVERY hero rotation, distributed (not
 * consecutive) through the first eight slides. Slot 0 is never touched so the
 * Shy Boy first-visit pin (and the returning-visitor slot-0 logic) is intact.
 */
const STAR_SLUG = "el-paso-star";
const STAR_SLOTS = [1, 4, 7];

const enforceStarSlots = (list: ScoredCandidate[]): ScoredCandidate[] => {
  const stars = list.filter((s) => s.slug === STAR_SLUG);
  if (stars.length === 0) return list;
  const others = list.filter((s) => s.slug !== STAR_SLUG);
  if (others.length === 0) return list;

  const out: ScoredCandidate[] = [];
  const starQueue = [...stars];
  const otherQueue = [...others];
  // Slot 0 always comes from the non-Star pool to preserve the existing pin.
  const total = list.length;
  for (let i = 0; i < total; i++) {
    if (STAR_SLOTS.includes(i) && starQueue.length > 0) {
      out.push(starQueue.shift()!);
    } else if (otherQueue.length > 0) {
      out.push(otherQueue.shift()!);
    } else if (starQueue.length > 0) {
      out.push(starQueue.shift()!);
    }
  }
  return out;
};

/**
 * Returns the ordered hero slide list for the current visitor.
 *
 * First visit  → Shy Boy ("first-visit" pin) on slot 0, editorial order after.
 * Every return → freshly shuffled rotation, Shy Boy never on slot 0 again.
 */
export const useHeroSlides = (): ScoredCandidate[] => {
  const [globalPop, setGlobalPop] = useState<Record<string, number>>({});
  const [isMobile, setIsMobile] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia?.("(max-width: 767px)").matches ?? false
      : false,
  );
  // Capture the first-visit decision ONCE per mount. Never mutate this ref
  // mid-session, otherwise `useMemo` recomputes (e.g. when globalPop resolves)
  // would flip Shy Boy off slot 0 before the user ever sees it.
  const firstVisitRef = useRef<boolean>(isFirstVisit());

  useEffect(() => {
    beginSession();
    if (firstVisitRef.current) markFirstVisitDone();
    if (typeof window !== "undefined" && window.matchMedia) {
      const mq = window.matchMedia("(max-width: 767px)");
      setIsMobile(mq.matches);
      const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
      mq.addEventListener?.("change", onChange);
      return () => mq.removeEventListener?.("change", onChange);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchGlobalPopularity().then((pop) => {
      if (!cancelled && pop && Object.keys(pop).length > 0) setGlobalPop(pop);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const taste = getTaste();
    const firstVisit = firstVisitRef.current;
    const pool = HERO_CANDIDATES.filter((c) => !c.mobileOnly || isMobile);
    const list = selectHeroSlides(pool, {
      taste,
      globalPopularity: globalPop,
      freshShuffle: !firstVisit,
    });
    if (list.length === 0) return list;

    if (firstVisit) {
      const firstVisitPin = list.find((s) => s.homepageHero === "first-visit");
      if (firstVisitPin) {
        const rest = list.filter((s) => s !== firstVisitPin);
        return enforceStarSlots([firstVisitPin, ...rest]);
      }
    }

    return enforceStarSlots(list);
  }, [globalPop, isMobile]);
};

